import { apiService } from './apiService';
import type { Position, Question } from '../../generated-src/client/positions/api';
import type { InterviewListItem as InterviewItem } from '../../generated-src/client/interviews/api';

// Простой кэш только для текущей сессии (без TTL)
const sessionCache = new Map<string, unknown>();

// Функция для получения данных из кэша сессии
function getFromSessionCache<T>(key: string): T | null {
  return (sessionCache.get(key) as T) || null;
}

// Функция для сохранения данных в кэш сессии
function setSessionCache<T>(key: string, data: T): void {
  sessionCache.set(key, data);
}



class OptimizedApiService {
  private candidateCache = new Map<number, any>();
  private positionCache = new Map<number, Position>();
  private questionsCache = new Map<number, Question[]>();

  // Кэшированная функция для получения кандидата
  private async getCandidateWithCache(id: number): Promise<any | null> {
    if (this.candidateCache.has(id)) {
      return this.candidateCache.get(id)!;
    }

    try {
              // Метод getCandidate удален - соответствующий API недоступен
        const candidate = null;
      this.candidateCache.set(id, candidate);
      return candidate;
    } catch (error) {
      console.error(`Error fetching candidate ${id}:`, error);
      return null;
    }
  }

  // Кэшированная функция для получения позиции
  private async getPositionWithCache(id: number): Promise<Position | null> {
    if (this.positionCache.has(id)) {
      return this.positionCache.get(id)!;
    }

    try {
      const position = await apiService.getPosition(id);
      this.positionCache.set(id, position);
      return position;
    } catch (error) {
      console.error(`Error fetching position ${id}:`, error);
      return null;
    }
  }

  // Получение интервью позиции постранично с сервера
  private async getPositionInterviewsPaged(positionId: number, page: number, size: number, opts?: { status?: string; result?: string; search?: string; sort?: string }): Promise<{ items: InterviewItem[]; total: number }> {
    try {
      return await apiService.getPositionInterviewsPaged(positionId, page, size, opts);
    } catch (error) {
      console.error(`Error fetching paged interviews for position ${positionId}:`, error);
      return { items: [], total: 0 };
    }
  }

  // Кэшированная функция для получения вопросов позиции
  private async getPositionQuestionsWithCache(positionId: number): Promise<Question[]> {
    if (this.questionsCache.has(positionId)) {
      return this.questionsCache.get(positionId)!;
    }

    try {
      const response = await apiService.getQuestions(positionId);
      const questions = response?.questions || [];
      this.questionsCache.set(positionId, questions);
      return questions;
    } catch (error) {
      console.error(`Error fetching questions for position ${positionId}:`, error);
      return [];
    }
  }

  // Очистка кэша
  clearCache() {
    this.candidateCache.clear();
    this.positionCache.clear();
    this.questionsCache.clear();
    sessionCache.clear();
  }

  // Batch загрузка кандидатов
  async getCandidatesBatch(ids: number[]): Promise<Map<number, any>> {
    const result = new Map<number, any>();
    const uncachedIds: number[] = [];

    // Проверяем кэш сессии
    ids.forEach(id => {
      const cached = getFromSessionCache<any>(`candidate:${id}`);
      if (cached) {
        result.set(id, cached);
      } else {
        uncachedIds.push(id);
      }
    });

    // Загружаем только некэшированные
    if (uncachedIds.length > 0) {
      const candidates = await Promise.all(
        uncachedIds.map(id =>
          // Метод getCandidate удален - соответствующий API недоступен
        Promise.resolve(null)
        )
      );

      // Кэшируем и добавляем в результат
      candidates.forEach((candidate: any, index: number) => {
        if (candidate) {
          const id = uncachedIds[index];
          setSessionCache(`candidate:${id}`, candidate);
          result.set(id, candidate);
        }
      });
    }

    return result;
  }

  // Batch загрузка позиций
  async getPositionsBatch(ids: number[]): Promise<Map<number, Position>> {
    const result = new Map<number, Position>();
    const uncachedIds: number[] = [];

    // Проверяем кэш сессии
    ids.forEach(id => {
      const cached = getFromSessionCache<Position>(`position:${id}`);
      if (cached) {
        result.set(id, cached);
      } else {
        uncachedIds.push(id);
      }
    });

    // Загружаем только некэшированные
    if (uncachedIds.length > 0) {
      const positions = await Promise.all(
        uncachedIds.map(id =>
          apiService.getPosition(id).catch(() => null)
        )
      );

      // Кэшируем и добавляем в результат
      positions.forEach((position, index) => {
        if (position) {
          const id = uncachedIds[index];
          setSessionCache(`position:${id}`, position);
          result.set(id, position);
        }
      });
    }

    return result;
  }

  // ТОЛЬКО позиции - остальное загружается при выборе
  async getVacanciesPageData(params?: Record<string, unknown>) {
    console.log('🔄 Загружаем ТОЛЬКО позиции (ленивая загрузка)');

    // ТОЛЬКО основной запрос позиций
    const positionsResponse = await apiService.getPositions(params);

    return {
      positions: positionsResponse.items,
      interviews: [], // НЕ загружаем - только при выборе позиции
      questions: {}, // НЕ загружаем - только при выборе позиции
      total: positionsResponse.total
    };
  }

  // Загрузка интервью для конкретной позиции (по требованию) — серверная пагинация
  async getPositionInterviewsOnDemand(positionId: number, page: number, size: number, opts?: { status?: string; result?: string; search?: string; sort?: string }): Promise<{ items: InterviewItem[]; total: number }> {
    const base = await this.getPositionInterviewsPaged(positionId, page, size, opts);

    const itemsWithEmail = await Promise.all(
      base.items.map(async (interview: any) => {
        const existingEmail =
          interview.candidateEmail ||
          interview.email;

        if (existingEmail) {
          return interview;
        }

        try {
          const details = await apiService.getInterviewAnswers(interview.id);
          const metaEmail =
            (details as any)?.candidate?.email ||
            (details as any)?.candidateEmail ||
            (details as any)?.email ||
            '';

          return {
            ...interview,
            email: metaEmail,
          };
        } catch {
          return interview;
        }
      })
    );

    return {
      items: itemsWithEmail as InterviewItem[],
      total: base.total,
    };
  }

  // Загрузка вопросов для конкретной позиции (по требованию)
  async getPositionQuestionsOnDemand(positionId: number): Promise<Question[]> {
    return this.getPositionQuestionsWithCache(positionId);
  }

  // Получение позиции по ID (с кэшированием)
  async getPositionById(positionId: number): Promise<Position> {
    const position = await this.getPositionWithCache(positionId);
    if (!position) {
      throw new Error(`Position with id ${positionId} not found`);
    }
    return position;
  }

  // ОДИН запрос для всех данных дашборда
  async getDashboardPageData(params?: Record<string, unknown>) {
    console.log('🔄 Загружаем данные дашборда (без предзагрузки интервью)');

    // Загружаем только позиции; интервью/кандидаты не предзагружаем
    const positionsResponse = await apiService.getPositions({ page: 1, size: 100 });

    return {
      positions: positionsResponse.items,
      interviews: [],
      candidates: [],
      stats: {
        total: 0,
        positionsCount: positionsResponse.total,
        candidatesCount: 0
      }
    };
  }

  // ОДИН запрос для всех данных страницы интервью
  async getInterviewsPageData(params?: Record<string, unknown>) {
    console.log('🔄 Загружаем данные страницы интервью (batch запросы)');

    // Основной запрос интервью
    const interviewsResponse = await apiService.getInterviews(params);

    // Получаем уникальные ID позиций и кандидатов
    const positionIds = Array.from(new Set(interviewsResponse.items.map((i: InterviewItem) => i.positionId)));
    const candidateIds = Array.from(new Set(interviewsResponse.items.map((i: InterviewItem) => i.candidateId)));

    // Batch запросы с кэшированием
    const [positionsResults, candidatesResults] = await Promise.all([
      Promise.all(positionIds.map(id => this.getPositionWithCache(id))),
      Promise.all(candidateIds.map(id => this.getCandidateWithCache(id)))
    ]);

    return {
      interviews: interviewsResponse.items,
      positions: positionsResults.filter((p): p is Position => p !== null),
      candidates: candidatesResults.filter((c): c is any => c !== null),
      total: interviewsResponse.total
    };
  }

  // Получение всех интервью постранично
  async getAllInterviews(page: number, size: number): Promise<{ items: InterviewItem[]; total: number }> {
    try {
      return await apiService.getAllInterviewsPaged(page, size);
    } catch (error) {
      console.error('Error fetching all interviews:', error);
      return { items: [], total: 0 };
    }
  }

  // Очистка кэша сессии
  clearSessionCache(pattern?: string) {
    if (pattern) {
      // Удаляем только ключи, соответствующие паттерну
      const keys = Array.from(sessionCache.keys());
      for (const key of keys) {
        if (key.includes(pattern)) {
          sessionCache.delete(key);
        }
      }
    } else {
      // Очищаем весь кэш сессии
      sessionCache.clear();
    }
  }

  // Получение статистики кэша
  getCacheStats() {
    return {
      size: sessionCache.size,
      keys: Array.from(sessionCache.keys())
    };
  }
}

// Экспортируем единственный экземпляр
export const optimizedApiService = new OptimizedApiService();
